import React, { useState, useEffect } from "react";
import styles from "./About.module.css";
import aboutImg from "../../../assets/images/about-bg.png";
import api from "../../../api/axios";
import {
  User,
  Eye,
  Target,
  Building2,
  ShieldCheck,
  Users,
  Leaf,
} from "lucide-react";

const About = () => {
  const [stats, setStats] = useState({ families: 0, amenities: 0, years: 0 });
  const [committee, setCommittee] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/public/stats');
        setStats(data);
      } catch (error) {
        console.error("Failed to fetch stats", error);
      }
    };
    
    const fetchCommittee = async () => {
      try {
        const { data } = await api.get('/public/committee');
        setCommittee(data);
      } catch (error) {
        console.error("Failed to fetch committee", error);
      }
    };

    fetchStats();
    fetchCommittee();
  }, []);

  return (
    <div className={styles.aboutPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContent}>
          <div className={styles.pillBadge}>◈ ABOUT OUR SOCIETY ◈</div>
          <h1 className={styles.heroTitle}>
            About Our
            <br />
            Society
          </h1>
          <div className={styles.titleDivider}></div>
          <p className={styles.heroDescription}>
            Sanskriti Shubh Aangan is more than just a place to live – It's a
            community built on trust, respect and togetherness. Our mission is
            to create a safe, secure and harmonious environment where every
            resident feels at home.
          </p>
        </div>
        <div className={styles.heroImageContainer}>
          <div className={styles.imageFrame}>
            <img src={aboutImg} alt="Society" className={styles.aboutImage} />
            <div className={styles.imageTag}>
              TOGETHER WE BUILD
              <br />A BETTER COMMUNITY
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className={styles.visionMissionSection}>
        <div className={styles.vmCard}>
          <div className={styles.vmIcon}>
            <Eye size={28} color="var(--white)" />
          </div>
          <h2>Our Vision</h2>
          <p>
            A safe, connected and harmonious community where every resident
            feels at home.
          </p>
          <div className={styles.vmDivider}></div>
        </div>
        <div className={styles.vmCard}>
          <div className={styles.vmIcon}>
            <Target size={28} color="var(--white)" />
          </div>
          <h2>Our Mission</h2>
          <p>
            Technology led society management for simple, transparent and
            accessible living.
          </p>
          <div className={styles.vmDivider}></div>
        </div>
      </section>

      {/* Community Values */}
      <section className={styles.valuesSection}>
        <h2 className={styles.sectionTitle}>Community Values</h2>
        <div className={styles.valuesGrid}>
          <div className={styles.valueCard}>
            <div className={styles.valueIcon}>
              <ShieldCheck size={28} color="var(--primary-green)" />
            </div>
            <h3>Trust & Transparency</h3>
            <p>Open communication and clear operations.</p>
          </div>
          <div className={styles.valueCard}>
            <div className={styles.valueIcon}>
              <Users size={28} color="var(--primary-green)" />
            </div>
            <h3>Inclusivity</h3>
            <p>Welcoming all families, together we grow.</p>
          </div>
          <div className={styles.valueCard}>
            <div className={styles.valueIcon}>
              <Leaf size={28} color="var(--primary-green)" />
            </div>
            <h3>Sustainability</h3>
            <p>Eco-friendly practices for a greener tomorrow.</p>
          </div>
        </div>
      </section>

      {/* Society Infrastructure */}
      <section className={styles.infrastructureSection}>
        <div className={styles.infraIconWrap}>
          <div className={styles.infraIconCircle}>
            <img src={aboutImg} alt="Infrastructure" />
          </div>
        </div>
        <div className={styles.infraContent}>
          <h2 className={styles.infraTitle}>Society Infrastructure</h2>
          <div className={styles.infraTitleDivider}></div>
          <p className={styles.infraDesc}>
            Modern architecture with 25+ spacious apartments across 4 towers,
            complemented by premium amenities, landscaped gardens, 24/7 water
            supply, power backup, and top-tier security systems.
          </p>
        </div>
      </section>

      {/* Management Committee */}
      <section className={styles.committeeSection}>
        <h2 className={styles.sectionTitle}>Management Committee</h2>
        <div className={styles.committeeGrid}>
          {committee.length > 0 ? (
            committee.map((member) => (
              <div key={member._id} className={`${styles.committeeMember} ${member.isHead ? styles.headMember : ''}`}>
                <div className={styles.memberAvatar}>
                  {member.image ? (
                    <img src={member.image} alt={member.name} />
                  ) : (
                    <div style={{width: '100%', height: '100%', background: '#154530', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      <User size={64} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <h3>{member.name}</h3>
                <p>{member.role}</p>
              </div>
            ))
          ) : (
            <p style={{textAlign: 'center', width: '100%', color: '#6b7280'}}>Committee members will be updated soon.</p>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsWrapper}>
        <div className={styles.statsCard}>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>{stats.years}+</span>
            <span className={styles.statLabel}>Years of Togetherness</span>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>{stats.families}+</span>
            <span className={styles.statLabel}>Happy Families</span>
          </div>
          <div className={styles.statDivider}></div>
          <div className={styles.statItem}>
            <span className={styles.statNumber}>{stats.amenities}+</span>
            <span className={styles.statLabel}>Amenities & Facilities</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
